import { MouseEvent } from 'react'

export function union<T>(source?: T[], target?: T[]) {
  return [...new Set([...(target || []), ...(source || [])])]
}

export function difference<T>(source?: T[], target?: T[]) {
  if (!target) {
    return source
  }
  return source?.filter((item) => !target.includes(item))
}

export function getIsIndeterminate<T>(selected?: T[], all?: T[]) {
  return selected && selected.length > 0 && !getAllChecked(selected, all)
}

export function getAllChecked<T>(selected?: T[], all?: T[]) {
  if (all === undefined) {
    return true
  }
  if (selected === undefined) {
    return false
  }
  if (selected.length < all.length) {
    return false
  }
  return all.every((value) => selected.includes(value))
}

export interface ChangeRadioValuesParams<Value> {
  value: Value,
  selectedValues: Value[],
  setSelectedValues?: (values: Value[]) => void,
  maxSelectedValues?: number
  stopPropagation?: boolean
  compareFn?: (a: Value, b: Value) => boolean
}

export function changeRadioValues<Value>({
  value,
  selectedValues,
  setSelectedValues,
  maxSelectedValues,
  stopPropagation,
  compareFn,
}: ChangeRadioValuesParams<Value>) {
  return (checked: boolean, event?: MouseEvent<HTMLElement>) => {
    if (stopPropagation && event) {
      event.stopPropagation()
    }
    if (!setSelectedValues) { return }

    if (!checked) {
      setSelectedValues(selectedValues.filter(item => compareFn ? !compareFn(item, value) : item !== value))
      return
    }
    if (maxSelectedValues !== undefined && selectedValues.length >= maxSelectedValues) {
      return
    }
    setSelectedValues(selectedValues.concat(value))
  }
}

export function replaceInArray<T>(array: T[], newItem: T, compare: (a: T, b: T) => boolean): T[] {
  return array.map(item => compare(item, newItem) ? newItem : item)
}

export function concatWithReplace<T>(
  array1: T[],
  array2: T[],
  compare: (a: T, b: T) => boolean,
): T[] {
  const result = array1.slice()

  array2.forEach(newItem => {
    const index = result.findIndex(item => compare(item, newItem))
    if (index !== -1) {
      result[index] = newItem
    } else {
      result.push(newItem)
    }
  })

  return result
}

export const rejectUndefinedAndNull = <T>(
  item: T | undefined | null,
): item is T => item !== undefined && item !== null

export async function asyncEvery<T>(array: T[], callback: (item: T) => Promise<boolean>) {
  for (const element of array) {
    if (!await callback(element)) {
      return false
    }
  }
  return true
}

